import { useParams } from 'react-router-dom';
import { LanguageType } from 'types';
import { useLanguage } from 'features/hooks/useLanguage';
import { useGetCodelistForEntryQuery } from 'features/codelist/data/useGetCodelistForEntry';
import {
	CodelistRecord,
	createCodelistRecordSchema
} from 'features/codelist/domain/codelistRecordSchema';
import { Input, InputSkeleton } from 'features/entry-form-v2/component/Input';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'features/components/Button';
import { useCreateCodelistRecordMutation } from 'features/codelist/data/useCreateCodelistRecordMutation';
import { useUpdateCodelistForEntryMutation } from 'features/codelist/data/useUpdateCodelistForEntryMutation';
import clsx from 'clsx';
import { CloseIcon } from '@icons';
import { IconButton } from 'features/entry-form-v2/component/IconButton';
import { useState } from 'react';
import { CodelistDisplay } from './display/CodelistDisplay';
import { CODELIST_SIDEBAR_DICTIONARY } from './Dictionary';

export const CodelistSidebar = ({
	originalEntryId,
	open,
	onClose
}: {
	originalEntryId: string;
	open: boolean;
	onClose: () => void;
}) => {
	const params = useParams();
	const projectId = params.projectId as string;

	const language = useLanguage();
	const translation = DICTIONARY[language];

	const getCodelistForEntryQuery = useGetCodelistForEntryQuery(
		{
			entryId: originalEntryId,
			projectId: projectId
		},
		{
			enabled: open
		}
	);

	return (
		<div
			className={clsx(
				'fixed top-[60px] right-0 bottom-0 bg-gray-100 z-50 w-[300px] p-10 transition-transform duration-100 ease-in',
				{
					'translate-x-[300px]': !open,
					'translate-x-0': open
				}
			)}
		>
			<div className="flex justify-between items-center mb-10">
				<h2 className="text-lg font-bold">{translation.title}</h2>

				<IconButton onClick={onClose}>
					<CloseIcon className="size-6" />
				</IconButton>
			</div>

			{getCodelistForEntryQuery.isLoading && (
				<div className="flex flex-col gap-2" role="progressbar">
					<InputSkeleton />
					<InputSkeleton />
				</div>
			)}

			{getCodelistForEntryQuery.data?.type === 'success' && (
				<Content
					originalEntryId={originalEntryId}
					projectId={projectId}
					initialCodelist={getCodelistForEntryQuery.data.record}
				/>
			)}

			{getCodelistForEntryQuery.data?.type === 'error' &&
				getCodelistForEntryQuery.data.error === 'codelist-not-found' && (
					<CreateCodelistForm
						originalEntryId={originalEntryId}
						projectId={projectId}
						onCancelClicked={onClose}
					/>
				)}
		</div>
	);
};

const Content = ({
	originalEntryId,
	projectId,
	initialCodelist
}: {
	originalEntryId: string;
	projectId: string;
	initialCodelist: CodelistRecord;
}) => {
	const [isEditing, setIsEditing] = useState(false);

	return (
		<div>
			{isEditing ? (
				<UpdateCodelistForm
					originalEntryId={originalEntryId}
					projectId={projectId}
					initialCodelist={initialCodelist}
					onCancelClicked={() => setIsEditing(false)}
				/>
			) : (
				<CodelistDisplay
					codelist={initialCodelist}
					onEditClicked={() => setIsEditing(true)}
				/>
			)}
		</div>
	);
};

const UpdateCodelistForm = ({
	originalEntryId,
	projectId,
	initialCodelist,
	onCancelClicked
}: {
	originalEntryId: string;
	projectId: string;
	initialCodelist: CodelistRecord;
	onCancelClicked: () => void;
}) => {
	const updateCodelistForEntryMutation = useUpdateCodelistForEntryMutation({
		originalEntryId: originalEntryId,
		projectId: projectId
	});

	return (
		<CodelistForm
			onSubmit={async data => {
				await updateCodelistForEntryMutation.mutateAsync(data);
				onCancelClicked();
			}}
			initialCodelist={initialCodelist}
			onCancelClicked={onCancelClicked}
			loading={updateCodelistForEntryMutation.isLoading}
		/>
	);
};

const CreateCodelistForm = ({
	originalEntryId,
	projectId,
	onCancelClicked
}: {
	originalEntryId: string;
	projectId: string;
	onCancelClicked: () => void;
}) => {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	const createCodelistForEntryMutation = useCreateCodelistRecordMutation({
		originalEntryId: originalEntryId,
		projectId: projectId
	});

	const submit = async (data: CodelistRecord) => {
		try {
			await createCodelistForEntryMutation.mutateAsync(data);
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<CodelistForm
			onSubmit={submit}
			onCancelClicked={onCancelClicked}
			loading={createCodelistForEntryMutation.isLoading}
			error={
				createCodelistForEntryMutation.error
					? translation.createCodelistForm.errors.generic
					: undefined
			}
		/>
	);
};

const CodelistForm = ({
	onSubmit,
	initialCodelist,
	onCancelClicked,
	loading,
	error
}: {
	onSubmit: (data: CodelistRecord) => void;
	initialCodelist?: CodelistRecord;
	onCancelClicked: () => void;
	loading: boolean;
	error?: string;
}) => {
	const language = useLanguage();
	const localTranslation = DICTIONARY[language];
	const sharedTranslation = CODELIST_SIDEBAR_DICTIONARY[language];

	const form = useForm<CodelistRecord>({
		resolver: zodResolver(createCodelistRecordSchema(language)),
		defaultValues: initialCodelist
	});

	return (
		<form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
			<Input
				label={sharedTranslation.customId.label}
				id="customId"
				{...form.register('customId')}
				error={form.formState.errors.customId?.message}
			/>

			<Input
				label={sharedTranslation.ssn.label}
				autoFocus
				id="ssn"
				{...form.register('ssn')}
			/>

			<Input
				label={sharedTranslation.firstName.label}
				inputClassName="capitalize"
				id="firstName"
				{...form.register('firstName')}
				error={form.formState.errors.firstName?.message}
			/>

			<Input
				label={sharedTranslation.lastName.label}
				inputClassName="capitalize"
				id="lastName"
				{...form.register('lastName')}
				error={form.formState.errors.lastName?.message}
			/>

			<Input
				label={sharedTranslation.dateOfBirth.label}
				id="dateOfBirth"
				{...form.register('dateOfBirth')}
				type="date"
				error={form.formState.errors.dateOfBirth?.message}
			/>

			<Input
				label={sharedTranslation.mobilePhone.label}
				id="mobilePhone"
				{...form.register('mobilePhone')}
				error={form.formState.errors.mobilePhone?.message}
			/>

			<Input
				label={sharedTranslation.email.label}
				id="email"
				{...form.register('email')}
				error={form.formState.errors.email?.message}
			/>

			<Input
				label={sharedTranslation.address.label}
				id="address"
				{...form.register('address')}
				error={form.formState.errors.address?.message}
			/>

			<Input
				label={sharedTranslation.postalCode.label}
				id="postalCode"
				{...form.register('postalCode')}
				error={form.formState.errors.postalCode?.message}
			/>

			<Input
				label={sharedTranslation.postalPlace.label}
				id="postalPlace"
				{...form.register('postalPlace')}
				error={form.formState.errors.postalPlace?.message}
			/>

			<Input
				label={sharedTranslation.country.label}
				id="country"
				{...form.register('country')}
				error={form.formState.errors.country?.message}
			/>

			{error && <p className="text-red-500 text-sm font-bold text-center my-2">{error}</p>}

			<div className="flex flex-col gap-4 justify-end">
				<Button
					type="submit"
					variant="primary"
					text={localTranslation.submitButton}
					loading={loading}
				/>

				<Button
					type="button"
					variant="secondary"
					text={localTranslation.cancelButton}
					onClick={onCancelClicked}
				/>
			</div>
		</form>
	);
};

const DICTIONARY = {
	[LanguageType.English]: {
		title: 'Codelist',

		submitButton: 'Save',
		cancelButton: 'Cancel',
		editButton: 'Edit',

		createCodelistForm: {
			errors: {
				generic: 'Could not create codelist'
			}
		}
	},
	[LanguageType.Norwegian]: {
		title: 'Kodeliste',

		submitButton: 'Lagre',
		cancelButton: 'Avbryt',
		editButton: 'Rediger',

		createCodelistForm: {
			errors: {
				generic: 'Kunne ikke opprette kodeliste'
			}
		}
	}
};
